import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { MDSModal } from "@/mds";
import type {
  AppId,
  WelcomeWizardGroup,
  WelcomeWizardProps,
  WelcomeStepContext,
} from "./WelcomeWizard.types";
import { WizardContainer } from "./WelcomeWizard.styles";
import { AnimatePresence, motion } from "motion/react";
import { Steps } from "./WelcomeWizard.config";
import styled from "@emotion/styled";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

export const WelcomeWizard = observer<WelcomeWizardProps>(
  ({ isOpen, onClose, initialStep = "app-selection" }) => {
    // Generic context state instead of import-specific state
    const [stepContext, setStepContext] = useState<WelcomeStepContext>({
      // Import-specific data
      wizardType: "welcome",
      selectedAppId: null as AppId | null,
      customAppName: "",
      // Can be extended for other step types
    });

    const [contentHeight, setContentHeight] = useState<number>(360);
    const wizardContainerRef = useRef<HTMLDivElement>(null);
    const [wizardStep, _setWizardStep] = useState<string>(initialStep);
    const expStepRef = useRef<HTMLDivElement>(null);

    const currentStepConfig = useMemo(() => {
      return Steps[wizardStep];
    }, [wizardStep]);

    const setHeightForStep = useCallback(
      (step: string) => {
        const doWork = () => {
          if (currentStepConfig?.fullHeight) {
            setContentHeight(5000);
            return;
          }

          const stepEl = document.querySelector(
            `[data-wizard-step="${step}"]`
          ) as HTMLElement | null;
          if (stepEl) {
            const vPadding = 56; // 28 top and bottom
            const newHeight = stepEl.scrollHeight + vPadding;
            setContentHeight(newHeight);
          } else {
            requestAnimationFrame(doWork);
          }
        };
        requestAnimationFrame(doWork);
      },
      [currentStepConfig]
    );

    useEffect(() => {
      if (isOpen) {
        setHeightForStep(wizardStep);
      }
    }, [isOpen, setHeightForStep, wizardStep]);

    const gotoWizardStep = useCallback(
      (step: string, nextAppId?: AppId) => {
        _setWizardStep(step);

        trackEvent(TrackedEvent.ImportWizardStepChange, {
          from: wizardStep,
          to: step,
          appId: nextAppId ?? stepContext.selectedAppId,
        });
      },
      [stepContext.selectedAppId, wizardStep]
    );

    const handleClose = (group: WelcomeWizardGroup, ...rest: string[]) => {
      onClose(group, ...rest);
    };

    const handleModalClose = () => {
      // Prevent closing the modal via ESC or clicking outside
      // Only allow closing via the Skip button
      // There are explicit "close" buttons in the various app-specific flows
      return;
    };

    // Generic context update handler
    const handleUpdateContext = useCallback((updates: Partial<WelcomeStepContext>) => {
      setStepContext(prev => ({ ...prev, ...updates }));
    }, []);

    const handleTabSelect = useCallback(
      (appId: AppId) => {
        let nextWizardStep = "other-apps-1";

        switch (appId) {
          case "apple-notes":
            nextWizardStep = "apple-notes-1";
            handleUpdateContext({ selectedAppId: appId });
            setHeightForStep("apple-notes-1");
            break;
          case "obsidian":
            nextWizardStep = "obsidian-1";
            handleUpdateContext({ selectedAppId: appId });
            setHeightForStep("obsidian-1");
            break;
          case "other":
            nextWizardStep = "other-apps-1";
            handleUpdateContext({ selectedAppId: appId });
            setHeightForStep("other-apps-1");
            break;
          default:
            nextWizardStep = "other-apps-1";
            handleUpdateContext({ selectedAppId: "other" });
            setHeightForStep("other-apps-1");
            break;
        }

        gotoWizardStep(nextWizardStep, appId);
      },
      [gotoWizardStep, handleUpdateContext, setHeightForStep]
    );

    const handleSkip = useCallback(() => {
      gotoWizardStep("skip-confirmation");
      setHeightForStep("skip-confirmation");
    }, [gotoWizardStep, setHeightForStep]);

    return (
      <MDSModal isOpen={isOpen} handleClose={handleModalClose}>
        <ModalContent>
          <WizardContainer
            data-component="wizard-container"
            layout="position"
            layoutRoot
            layoutScroll
            animate={{ height: contentHeight }}
            ref={wizardContainerRef}
          >
            {Object.entries(Steps).map(([stepName, stepConfig]) => {
              return (
                <AnimatePresence key={stepName}>
                  {wizardStep === stepName && (
                    <StepCrossFade
                      key={stepName}
                      layout="position"
                      ref={wizardStep === stepName ? expStepRef : undefined}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      top={"28px"}
                      data-wizard-step={stepName}
                    >
                      {stepConfig.render({
                        // New generic props
                        context: stepContext,
                        onUpdateContext: handleUpdateContext,
                        onNavToStep: (step: string) => {
                          gotoWizardStep(step);
                          setHeightForStep(step);
                        },
                        onClose: (...rest: string[]) => handleClose(stepConfig.group, ...rest),
                        onSkip: () => handleSkip(),

                        // Props for import steps
                        selectedAppId: stepContext.selectedAppId || null,
                        customAppName: stepContext.customAppName || "",
                        onSetSelectedApp: (appId: AppId) =>
                          handleUpdateContext({ selectedAppId: appId }),
                        onSetCustomAppName: (name: string) =>
                          handleUpdateContext({ customAppName: name }),
                        handleTabSelect,
                      })}
                    </StepCrossFade>
                  )}
                </AnimatePresence>
              );
            })}
          </WizardContainer>
        </ModalContent>
      </MDSModal>
    );
  }
);

const StepCrossFade = styled(motion.div)<{ top: string }>`
  position: fixed;
  display: flex;
  top: ${({ top }) => top};
  left: ${({ theme }) => theme.spacing.lg};
  right: ${({ theme }) => theme.spacing.lg};
  max-height: calc(100% - 48px);
`;

const ModalContent = styled.div`
  overflow: hidden;
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
`;
